import { ChangeEvent, SyntheticEvent, useId, useState } from 'react';
import { ratioDeviatesStrongly } from '../utils/imageRatio';
import LabelWithHelp from './LabelWithHelp';

// A single image slot: a fixed-ratio preview, a replace button, and a remove
// button. Owns its own hidden file input so several slots stay independent.
// Extracted from BannerFormModal when the Services product form became its
// second consumer.
//
// The choose control is a <label htmlFor> bound to the input, NOT a button
// calling input.click(): iOS Safari silently ignores programmatic clicks on
// display:none file inputs in some contexts (this slot sits inside a
// position:fixed modal), which left the "Bild auswählen" button dead on
// iPhones. Native label activation opens the picker everywhere; the input is
// sr-only (rendered, visually hidden) for the same reason.
interface ImageSlotProps {
    label: string;
    help: string;
    previewSrc: string | null;
    onSelect: (e: ChangeEvent<HTMLInputElement>) => void;
    onRemove: () => void;
    chooseLabel: string;
    removeLabel: string;
    noImageLabel: string;
    /** Preview aspect-ratio class. Set it to whatever the end-user app really
     *  renders: the banner slots pass the carousel's 16:7, the Services product
     *  image `aspect-square`, because every customer-facing surface crops a
     *  product photo to a square. The 16:9 default is for a slot that has not
     *  said. */
    aspectClass?: string;
    /** The slot's width ÷ height (16/7 for a banner). With `ratioWarning`, a
     *  picked or stored image whose format differs strongly gets that note
     *  under the preview. A warning only — the slot crops either way. */
    targetRatio?: number;
    ratioWarning?: string;
}

const ImageSlot = ({
    label,
    help,
    previewSrc,
    onSelect,
    onRemove,
    chooseLabel,
    removeLabel,
    noImageLabel,
    aspectClass = 'aspect-video',
    targetRatio,
    ratioWarning,
}: ImageSlotProps) => {
    const inputId = useId();
    // Keyed by the source it was measured on, so a new image never shows the
    // previous one's verdict while it loads.
    const [mismatch, setMismatch] = useState<{ src: string, deviates: boolean } | null>(null);
    const handleLoad = (e: SyntheticEvent<HTMLImageElement>) => {
        if (!targetRatio || !previewSrc) return;
        const { naturalWidth, naturalHeight } = e.currentTarget;
        setMismatch({ src: previewSrc, deviates: ratioDeviatesStrongly(naturalWidth, naturalHeight, targetRatio) });
    };
    const showRatioWarning = !!ratioWarning && !!previewSrc
        && mismatch?.src === previewSrc && mismatch.deviates;
    return (
        <div className={'flex-1'}>
            <LabelWithHelp text={label} help={help} />
            <div
                className={
                    `w-full ${aspectClass} rounded-md border border-foreground/15 bg-muted overflow-hidden flex items-center justify-center`
                }
            >
                {previewSrc ? (
                    <img
                        src={previewSrc}
                        alt={label}
                        className={'w-full h-full object-cover'}
                        onLoad={handleLoad}
                    />
                ) : (
                    <span className={'text-sm text-foreground/40'}>{noImageLabel}</span>
                )}
            </div>
            {showRatioWarning && (
                <p className={'mt-2 text-sm text-foreground/70'} role={'status'}>{ratioWarning}</p>
            )}
            <input
                id={inputId}
                type={'file'}
                accept={'image/png,image/jpeg,image/webp,image/svg+xml'}
                className={'sr-only'}
                onChange={onSelect}
            />
            <div className={'mt-2 flex gap-2'}>
                <label
                    htmlFor={inputId}
                    className={
                        'cursor-pointer rounded-md border border-foreground/15 px-3 py-2 text-sm font-medium text-foreground/70 hover:text-foreground'
                    }
                >
                    {chooseLabel}
                </label>
                {previewSrc && (
                    <button
                        type={'button'}
                        onClick={onRemove}
                        className={
                            'rounded-md border border-foreground/15 px-3 py-2 text-sm font-medium text-foreground/70 hover:text-foreground'
                        }
                    >
                        {removeLabel}
                    </button>
                )}
            </div>
        </div>
    );
};

export default ImageSlot;
